import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Button,
  Box,
  TextField,
  CircularProgress,
  Divider,
  Alert,
  IconButton,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Formik, Form, Field, useFormikContext, FieldArray } from "formik";
import * as Yup from "yup";
import { getValidToken } from "../../../utils/authUtils";
import { allocationApi } from "../../../project/project_module/services/apiService";
import { AllocationCategory } from "../../../project/project_module";
import {
  Add as AddIcon,
  WhatsApp as WhatsAppIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { commonTextFieldProps, smallButtonProp } from "../../../utils/styles";
import { vehicleExpenseApi } from "../apiService";
import { useToast } from "../../../components/Toast";
import { Loading } from "../../../components/Loading";
import { ExpenseFormData } from "./types";

interface Driver {
  name: string;
  contactNumber: string;
}

interface DriverFormData {
  drivers: Driver[];
}

const validationSchema = Yup.object({
  drivers: Yup.array()
    .of(
      Yup.object({
        name: Yup.string()
          .required("Driver name is required")
          .min(2, "Name must be at least 2 characters")
          .max(50, "Name must be less than 50 characters"),
        contactNumber: Yup.string()
          .required("Contact number is required")
          .min(10, "Contact number must be at least 10 digits")
          .max(10, "Contact number must be at most 10 digits"),
      })
    )
    .min(1, "At least one driver is required"),
});

const SendWhatsappModal = ({
  open,
  onClose,
  projectId,
  vehicleId,
  lastReadingDate,
  currentReadingDate,
  average,
  vehicleNumber,
  expenseData,
  lastOdometerReading,
  index,
}: {
  open: boolean;
  onClose: () => void;
  projectId?: string;
  vehicleId?: string;
  lastReadingDate?: string;
  currentReadingDate?: string;
  average?: number;
  vehicleNumber?: string;
  expenseData?: any;
  lastOdometerReading?: number;
  index: number;
}) => {
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();
  const { values, setFieldValue } = useFormikContext<ExpenseFormData>();
  const [initialDrivers, setInitialDrivers] = useState<Driver[]>(values?.expenses?.[index]?.whatsappData?.drivers || []);

  const fetchProjectDrivers = async () => {
    if (!projectId || !vehicleId) return;

    setLoading(true);
    try {
      const allAllocations =
        await allocationApi.getAllocationsByProjectAndVehicle(
          projectId,
          vehicleId,
          lastReadingDate || "",
          currentReadingDate || ""
        );

      // Extract driver information from allocations
      const driverList: Driver[] = [];

      if (allAllocations && Array.isArray(allAllocations)) {
        allAllocations.forEach((allocation: any) => {
          driverList.push({
            name: allocation.name,
            contactNumber: allocation.contactNumber,
          });
        });
      }

      setInitialDrivers(driverList);

      // If no drivers found, add an empty driver form
      if (driverList.length === 0) {
        setInitialDrivers([{ name: "", contactNumber: "" }]);
      }
    } catch (error) {
      setInitialDrivers([{ name: "", contactNumber: "" }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: DriverFormData) => {
    try {
      setIsSubmitting(true);
      setFieldValue(`expenses.${index}.sendWhatsappToDriver`, true);
      setFieldValue(`expenses.${index}.whatsappData`, {
        average,
        lastReadingDate,
        currentReadingDate,
        lastOdometerReading,
        drivers: values.drivers,
        vehicleNumber,
      });
      setFieldValue(`expenses.${index}.isEdited`, true);
      onClose();
    } catch (error) {
      return;
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (open && projectId && vehicleId) {
      fetchProjectDrivers();
    }
  }, [open, projectId, vehicleId]);

  if (isSubmitting) {
    return <Loading message="Sending WhatsApp messages..." />;
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ py: 0.5, px: 1, mb: 1 }}>
        <Typography
          variant="h6"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontSize: "1rem",
          }}
        >
          Send Whatsapp to Drivers
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={4}
          >
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading drivers...
            </Typography>
          </Box>
        ) : (
          <>
            <Formik
              initialValues={{ drivers: initialDrivers }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ values, errors, touched, isValid, dirty }) => (
                <Form>
                  <FieldArray name="drivers">
                    {({ push, remove }) => (
                      <Box display="flex" flexDirection="column" gap={2}>
                        {values.drivers.map((driver, driverIndex) => (
                          <>
                            <Box display="flex" flexDirection="row" gap={2} sx={{ mt: 1 }}>
                              <Field name={`drivers.${driverIndex}.name`}>
                                {({ field }: any) => (
                                  <TextField
                                    {...field}
                                    label="Driver Name"
                                    variant="outlined"
                                    fullWidth
                                    // error={
                                    //   touched.drivers?.[driverIndex]?.name &&
                                    //   !!errors.drivers?.[driverIndex]?.name
                                    // }
                                    // helperText={
                                    //   touched.drivers?.[driverIndex]?.name &&
                                    //   errors.drivers?.[driverIndex]?.name
                                    // }
                                    placeholder="Enter driver's full name"
                                    {...commonTextFieldProps}
                                    required
                                  />
                                )}
                              </Field>

                              <Field
                                name={`drivers.${driverIndex}.contactNumber`}
                              >
                                {({ field }: any) => (
                                  <TextField
                                    {...field}
                                    label="Contact Number"
                                    variant="outlined"
                                    fullWidth
                                    // error={
                                    //   touched.drivers?.[driverIndex]?.contactNumber &&
                                    //   !!errors.drivers?.[driverIndex]?.contactNumber
                                    // }
                                    // helperText={
                                    //   touched.drivers?.[driverIndex]?.contactNumber &&
                                    //   errors.drivers?.[driverIndex]?.contactNumber
                                    // }
                                    placeholder="Enter 10-digit mobile number"
                                    inputProps={{
                                      maxLength: 10,
                                      pattern: "[0-9]*",
                                    }}
                                    {...commonTextFieldProps}
                                    required
                                  />
                                )}
                              </Field>

                              <IconButton
                                size="small"
                                onClick={() => remove(driverIndex)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </>
                        ))}

                        <Box display="flex" alignItems="center" flexDirection="column" gap={1}>
                          {values.drivers.length === 0 && (
                            <Typography variant="subtitle2" sx={{ fontSize: "0.75rem" }}>
                              Add Driver details to send whatsapp.
                            </Typography>
                          )}
                          <Button
                            type="button"
                            variant="text"
                            onClick={() =>
                              push({ name: "", contactNumber: "" })
                            }
                            startIcon={<AddIcon />}
                            {...smallButtonProp}
                          >
                            {values.drivers.length > 0
                              ? "Add Another Driver"
                              : "Add Driver"}
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </FieldArray>

                  <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
                    <Button
                      variant="outlined"
                      onClick={onClose}
                      {...smallButtonProp}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={!isValid || isSubmitting}
                      {...smallButtonProp}
                    >
                      {isSubmitting ? "Sending..." : "Confirm"}
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SendWhatsappModal;
